import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Form, Spinner, Alert, Modal, Badge } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../../../../services/instance';
import toast from "react-hot-toast";
import { MdDelete, MdEditSquare } from "react-icons/md";
import Select from 'react-select';
import { useDispatch } from "react-redux";
import { CgTranscript } from "react-icons/cg";
import { TiTick } from "react-icons/ti";
import { TbPlayerEject } from "react-icons/tb";
import { GrCertificate } from "react-icons/gr";
import CertificateViewModal from './CertificateViewModal';
import { BsFileEarmarkSpreadsheet } from "react-icons/bs";
import { exportKnoxTableToExcel } from './EBRCHelper';
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};
const KnockOffTable = () => {
  const { period_id } = useParams();

  // State management
  const [knoxData, setKnoxData] = useState([]);
  const [periodData, setPeriodData] = useState();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [irmOptions, setIrmOptions] = useState([]);
  const [sbOptions, setSbOptions] = useState([]);
  const [irmSearchTerm, setIrmSearchTerm] = useState('');
  const [sbSearchTerm, setSbSearchTerm] = useState('');
  const [irmLoading, setIrmLoading] = useState(false);
  const [sbLoading, setSbLoading] = useState(false);
  const dispatch = useDispatch();
  const debouncedIrmSearch = useDebounce(irmSearchTerm, 500);
  const debouncedSbSearch = useDebounce(sbSearchTerm, 500);
  const [showCertificateModal, setShowCertificateModal] = useState(false);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [clientEditPermission, setClientEditPermission] = useState(false);
  const [clientHasSubmitted, setClientHasSubmitted] = useState(false);
  const [clientSubmissionVerified, setClientSubmissionVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  useEffect(() => {
    fetchData();
  }, [period_id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Only fetch Knox Data - IRM/SB loaded by search functions
      const knoxResponse = await axiosInstance.post(
        `/api/v1/ebrc/client/knock/off/list/${period_id}`
      );
      const periodInfo = knoxResponse.data.data.irm_period || {};
      setPeriodData(periodInfo);
      setClientEditPermission(periodInfo.client_edit_permission || false);
      setClientHasSubmitted(periodInfo.client_has_submitted || false);
      setClientSubmissionVerified(periodInfo.client_submission_verified || false);
      // Process Knox Data
      if (knoxResponse.data.type === 'success' && knoxResponse.data.data.lists.length > 0) {
        // Map MongoDB response to frontend structure
        const mappedKnoxData = knoxResponse.data.data.lists.map((record, index) => ({
          id: record.id,
          irm_id: record.irm_id,
          irmNumber: record.irmNumber,
          irmDate: record.irmDate,
          irmBalanceAmount: record.irmBalanceAmount,
          availableIRMBalance: record.availableIRMBalance,
          remitterName: record.remitterName,
          adCode: record.adCode,
          shipping_bill_id: record.shipping_bill_id,
          shippingBillNumber: record.shippingBillNumber,
          shippingBillDate: record.shippingBillDate,
          invoiceNumber: record.invoiceNumber,
          invoiceDate: record.invoiceDate,
          consigneeName: record.consigneeName,
          buyerName: record.buyerName,
          gstNumber: record.gstNumber,
          invoiceValue: record.invoiceValue,
          fobValue: record.fobValue,
          availableSBBalance: record.availableSBBalance,
          currency: record.currency,
          portCode: record.portCode,
          sbAdCode: record.sbAdCode,
          adjustedAmount: record.adjustedAmount,
          pendingBalanceInIRM: record.pendingBalanceInIRM,
          sbBalance: record.sbBalance,
          brcNumber: record.brcNumber || '',
          brcDate: record.brcDate || '',
          attachments: record.attachments || '',
          remarks: record.remarks || '',
          certificateUrl: record.certificatePreviewUrl || null,
          certificateDownloadUrl: record.certificateDownloadUrl || null,
          certificateFilename: record.certificateFilename || null,
          certificateUploadStatus: record.certificateUploadStatus || 'Pending',
          brcGenerated: record.brcGenerated || 0

        }));
        setKnoxData(mappedKnoxData);
        setTotalCount(knoxResponse.data.data.total_count || mappedKnoxData.length);
      } else {
        // Initialize with one empty row
        setKnoxData([createEmptyRow(1)]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.response?.data?.message || 'Error loading data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const searchIRMs = useCallback(async (searchTerm) => {
    try {
      setIrmLoading(true);

      const response = await axiosInstance.post(`/api/v1/ebrc/client/irm/list/${period_id}`, {
        page: 1,
        perpage: 50,
        search: searchTerm || '' // Backend uses 'search' parameter
      });

      if (response.data.type === 'success') {
        const options = response.data.data.lists.map(irm => ({
          value: irm.irm_number,
          label: irm.irm_number,
          data: {
            id: irm.id,
            irmNumber: irm.irm_number,
            irmDate: irm.irm_issue_date,
            balance: parseFloat(irm.balance_remaining),
            remitterName: irm.remittance_name,
            adCode: irm.remittance_ad_code,
            currency: irm.fcc
          }
        }));
        setIrmOptions(options);
      }
    } catch (err) {
      console.error('Error searching IRMs:', err);
      toast.error('Failed to search IRMs');
    } finally {
      setIrmLoading(false);
    }
  }, [period_id]);

  const searchShippingBills = useCallback(async (searchTerm) => {
    try {
      setSbLoading(true);

      const response = await axiosInstance.post(`/api/v1/ebrc/client/sb/list`, {
        page: 1,
        perpage: 50,
        q: searchTerm || '' // Backend uses 'q' parameter for search
      });

      if (response.data.type === 'success') {
        const options = response.data.data.lists.map(sb => ({
          value: sb.shipping_number,
          label: sb.shipping_number,
          data: {
            id: sb.id,
            sbNumber: sb.shipping_number,
            sbDate: sb.shipping_date,
            invoiceNumber: sb.invoice_number,
            invoiceDate: sb.invoice_date,
            consigneeName: sb.consignee_name,
            buyerName: sb.buyer_name,
            gstNumber: sb.gst_number,
            invoiceValue: parseFloat(sb.invoice_value),
            fobValue: parseFloat(sb.fob_value),
            currency: sb.currency,
            portCode: sb.port_code,
            adCode: sb.ad_code
          }
        }));
        setSbOptions(options);
      }
    } catch (err) {
      console.error('Error searching Shipping Bills:', err);
      toast.error('Failed to search Shipping Bills');
    } finally {
      setSbLoading(false);
    }
  }, [period_id]);

  // Trigger IRM search when debounced value changes
  useEffect(() => {
    searchIRMs(debouncedIrmSearch);
  }, [debouncedIrmSearch, searchIRMs]);

  // Trigger SB search when debounced value changes
  useEffect(() => {
    searchShippingBills(debouncedSbSearch);
  }, [debouncedSbSearch, searchShippingBills]);

  // Initial load of IRMs and SBs
  useEffect(() => {
    searchIRMs('');
    searchShippingBills('');
  }, [period_id]);


  const createEmptyRow = (id) => ({
    id: `temp_${Date.now()}_${Math.random()}`,
    irm_id: null, // Add this
    irmNumber: '',
    irmDate: '',
    irmBalanceAmount: 0,
    availableIRMBalance: 0,
    availableSBBalance: 0,
    remitterName: '',
    adCode: '',
    shipping_bill_id: null, // Add this
    invoiceNumber: '',
    invoiceDate: '',
    consigneeName: '',
    buyerName: '',
    gstNumber: '',
    invoiceValue: 0,
    fobValue: 0,
    currency: '',
    portCode: '',
    shippingBillNumber: '',
    shippingBillDate: '',
    sbAdCode: '',
    adjustedAmount: 0,
    pendingBalanceInIRM: 0,
    sbBalance: 0,
    brcNumber: '',
    brcDate: '',
    attachments: '',
    remarks: '',
    brcGenerated: 0
  });

  const calculateRemarks = (pendingIRMBalance, pendingSBBalance, row) => {
    const remarks = [];

    if (row.irmNumber && row.shippingBillNumber) {
      // Try to get IRM currency from options, fallback to row data
      const irmOption = irmOptions.find(opt => opt.value === row.irmNumber);
      const irmCurrency = irmOption ? irmOption.data.currency : row.adCode; // Fallback if not in options

      // Only check currency if we have both currencies
      if (irmCurrency && row.currency) {
        if (!validateCurrencyMatch(irmCurrency, row.currency)) {
          remarks.push(`⚠️ CURRENCY MISMATCH: IRM (${irmCurrency}) ≠ SB (${row.currency})`);
        }
      }
    }

    if (pendingIRMBalance === 0 && pendingSBBalance === 0) {
      remarks.push('IRM and Shipping Bill fully utilized');
    } else if (pendingIRMBalance === 0) {
      remarks.push(`IRM fully utilized, Shipping Bill balance: ${pendingSBBalance.toLocaleString()} ${row.currency || 'USD'}`);
    } else if (pendingSBBalance === 0) {
      remarks.push(`Shipping Bill fully utilized, IRM balance: ${pendingIRMBalance.toLocaleString()} ${row.currency || 'USD'}`);
    } else {
      remarks.push(`IRM balance: ${pendingIRMBalance.toLocaleString()} ${row.currency || 'USD'}, Shipping Bill balance: ${pendingSBBalance.toLocaleString()} ${row.currency || 'USD'}`);
    }

    return remarks.join('; ');
  };

  const validateCurrencyMatch = (irmCurrency, sbCurrency) => {
    if (!irmCurrency || !sbCurrency) return true; // Allow if either is empty
    return irmCurrency.toUpperCase() === sbCurrency.toUpperCase();
  };

  const findDuplicateCombinations = (rows) => {
    const combinations = {};
    const duplicates = [];

    rows.forEach((row, index) => {
      if (row.irmNumber && row.shippingBillNumber) {
        const key = `${row.irmNumber}_${row.shippingBillNumber}`;

        if (combinations[key]) {
          // Found duplicate
          duplicates.push({
            rowIndex: index + 1,
            firstOccurrence: combinations[key],
            irmNumber: row.irmNumber,
            shippingBillNumber: row.shippingBillNumber
          });
        } else {
          combinations[key] = index + 1;
        }
      }
    });

    return duplicates;
  };

  const isSavedRow = (row) => {
    // A row is "saved" if it has a valid MongoDB ID (24-character hex string)
    return row.id && typeof row.id === 'string' && row.id.length === 24 && !row.id.startsWith('temp_');
  };
  // Helper to create an option from saved row data
  const createSBOptionFromRow = (row) => {
    if (!row.shippingBillNumber) return null;

    return {
      value: row.shippingBillNumber,
      label: row.shippingBillNumber,
      data: {
        id: row.shipping_bill_id,
        sbNumber: row.shippingBillNumber,
        sbDate: row.shippingBillDate,
        invoiceNumber: row.invoiceNumber,
        invoiceDate: row.invoiceDate,
        consigneeName: row.consigneeName,
        buyerName: row.buyerName,
        gstNumber: row.gstNumber,
        invoiceValue: row.invoiceValue,
        fobValue: row.fobValue,
        currency: row.currency,
        portCode: row.portCode,
        adCode: row.sbAdCode
      }
    };
  };

  // Helper to create an IRM option from saved row data
  const createIRMOptionFromRow = (row) => {
    if (!row.irmNumber) return null;

    return {
      value: row.irmNumber,
      label: row.irmNumber,
      data: {
        id: row.irm_id,
        irmNumber: row.irmNumber,
        irmDate: row.irmDate,
        balance: row.irmBalanceAmount,
        remitterName: row.remitterName,
        adCode: row.adCode,
        currency: row.currency
      }
    };
  };

  const recalculateBalances = (data, fromIndex = 0) => {
    const updatedData = [...data];

    // Track IRM balances - Start with API balance (already reduced for saved rows)
    const irmBalances = {};
    irmOptions.forEach(opt => {
      irmBalances[opt.value] = opt.data.balance;
    });

    // Include balances from existing rows if not in options
    updatedData.forEach(row => {
      if (row.irmNumber && !irmBalances[row.irmNumber] && row.irmBalanceAmount > 0) {
        irmBalances[row.irmNumber] = row.irmBalanceAmount;
      }
    });

    // Track SB balances - Start with API balance
    const sbBalances = {};
    sbOptions.forEach(opt => {
      sbBalances[opt.value] = opt.data.invoiceValue;
    });

    // Include balances from existing rows if not in options
    updatedData.forEach(row => {
      if (row.shippingBillNumber && !sbBalances[row.shippingBillNumber] && row.invoiceValue > 0) {
        sbBalances[row.shippingBillNumber] = row.invoiceValue;
      }
    });

    // ✅ FIX 1: Only reduce balance for UNSAVED rows before fromIndex
    for (let i = 0; i < fromIndex; i++) {
      const row = updatedData[i];
      if (row.irmNumber && row.shippingBillNumber) {
        const adjustedAmount = parseFloat(row.adjustedAmount) || 0;

        // ✅ KEY FIX: Only reduce if row is NOT saved
        // Saved rows are already reflected in the balance from API
        if (adjustedAmount > 0) {
          // IRM: Only reduce for unsaved rows (backend already reduced for saved)
          if (!isSavedRow(row)) {
            irmBalances[row.irmNumber] = (irmBalances[row.irmNumber] || 0) - adjustedAmount;
          }
          // SB: Reduce for ALL rows (backend doesn't track SB allocations)
          sbBalances[row.shippingBillNumber] = (sbBalances[row.shippingBillNumber] || 0) - adjustedAmount;
        }
      }
    }

    // Recalculate from fromIndex onwards
    for (let i = fromIndex; i < updatedData.length; i++) {
      const row = updatedData[i];

      if (row.irmNumber && row.shippingBillNumber) {
        const currentIRMBalance = irmBalances[row.irmNumber] || 0;
        const currentSBBalance = sbBalances[row.shippingBillNumber] || 0;

        // Store available balance BEFORE adjustment
        row.availableIRMBalance = currentIRMBalance;
        row.availableSBBalance = currentSBBalance;

        // ✅ FIX 2: Don't auto-fill if user is manually editing
        // Only auto-fill for new rows or when not manually set
        const isCurrentlyEditing = row.adjustedAmount === '';

        if (!isCurrentlyEditing && (row.adjustedAmount === 0 || !row.manualAdjustment)) {
          const adjustedAmount = Math.min(currentIRMBalance, currentSBBalance);
          row.adjustedAmount = adjustedAmount;
        }

        // Calculate pending balances
        const adjustedAmount = parseFloat(row.adjustedAmount) || 0;
        const pendingIRMBalance = currentIRMBalance - adjustedAmount;
        const pendingSBBalance = currentSBBalance - adjustedAmount;

        row.pendingBalanceInIRM = Math.max(0, pendingIRMBalance);
        row.sbBalance = Math.max(0, pendingSBBalance);

        // Generate remarks
        row.remarks = calculateRemarks(row.pendingBalanceInIRM, row.sbBalance, row);

        // ✅ FIX 1: Only update balances for UNSAVED rows
        // Saved rows don't need to update the running balance
        if (adjustedAmount > 0) {
          // IRM: Only update for unsaved rows (backend tracks saved)
          if (!isSavedRow(row)) {
            irmBalances[row.irmNumber] = row.pendingBalanceInIRM;
          }
          // SB: ALWAYS update (backend doesn't track SB allocations)
          sbBalances[row.shippingBillNumber] = row.sbBalance;
        }
      }
    }

    return updatedData;
  };

  const handleIRMChange = (index, option) => {
    if (!option) {
      // Clear selection
      const updatedData = [...knoxData];
      updatedData[index] = {
        ...updatedData[index],
        irm_id: null,
        irmNumber: '',
        irmDate: '',
        irmBalanceAmount: 0,
        availableIRMBalance: 0,
        remitterName: '',
        adCode: '',
        currency: '',
      };
      setKnoxData(updatedData);
      return;
    }

    const selectedIRM = option.data;

    // Calculate current available balance for this IRM
    let currentAvailableBalance = selectedIRM.balance;

    // ✅ FIX: Only subtract UNSAVED rows before this index
    for (let i = 0; i < index; i++) {
      if (knoxData[i].irmNumber === selectedIRM.irmNumber && knoxData[i].adjustedAmount > 0) {
        // ✅ KEY FIX: Only reduce if row is NOT saved
        if (!isSavedRow(knoxData[i])) {
          currentAvailableBalance -= knoxData[i].adjustedAmount;
        }
      }
    }

    const updatedData = [...knoxData];
    updatedData[index] = {
      ...updatedData[index],
      irm_id: selectedIRM.id,
      irmNumber: selectedIRM.irmNumber,
      irmDate: selectedIRM.irmDate,
      irmBalanceAmount: selectedIRM.balance,
      availableIRMBalance: currentAvailableBalance,
      remitterName: selectedIRM.remitterName,
      adCode: selectedIRM.adCode,
      currency: selectedIRM.currency,
      manualAdjustment: false
    };

    setKnoxData(recalculateBalances(updatedData, index));
  };

  const handleSBChange = (index, option) => {
    if (!option) {
      // Clear selection
      const updatedData = [...knoxData];
      updatedData[index] = {
        ...updatedData[index],
        shipping_bill_id: null,
        shippingBillNumber: '',
        shippingBillDate: '',
        invoiceNumber: '',
        invoiceDate: '',
        consigneeName: '',
        buyerName: '',
        gstNumber: '',
        invoiceValue: 0,
        fobValue: 0,
        availableSBBalance: 0,
        portCode: '',
        sbAdCode: '',
      };
      setKnoxData(updatedData);
      return;
    }

    const selectedSB = option.data;

    // Calculate current available balance for this SB
    let currentAvailableBalance = selectedSB.invoiceValue;

    const currentRow = knoxData[index];
    if (currentRow.currency && selectedSB.currency) {
      if (!validateCurrencyMatch(currentRow.currency, selectedSB.currency)) {
        toast.error(`Currency mismatch! IRM is in ${currentRow.currency} but Shipping Bill is in ${selectedSB.currency}`);
        // Don't proceed with selection
        return;
      }
    }

    // ✅ FIX: Only subtract UNSAVED rows before this index
    for (let i = 0; i < index; i++) {
      if (knoxData[i].shippingBillNumber === selectedSB.sbNumber && knoxData[i].adjustedAmount > 0) {
        // ✅ KEY FIX: Only reduce if row is NOT saved
        if (!isSavedRow(knoxData[i])) {
          currentAvailableBalance -= knoxData[i].adjustedAmount;
        }
      }
    }

    const updatedData = [...knoxData];
    updatedData[index] = {
      ...updatedData[index],
      shipping_bill_id: selectedSB.id,
      shippingBillNumber: selectedSB.sbNumber,
      shippingBillDate: selectedSB.sbDate,
      invoiceNumber: selectedSB.invoiceNumber,
      invoiceDate: selectedSB.invoiceDate,
      consigneeName: selectedSB.consigneeName,
      buyerName: selectedSB.buyerName,
      gstNumber: selectedSB.gstNumber,
      invoiceValue: selectedSB.invoiceValue,
      fobValue: selectedSB.fobValue,
      availableSBBalance: currentAvailableBalance,
      currency: selectedSB.currency,
      portCode: selectedSB.portCode,
      sbAdCode: selectedSB.adCode,
      manualAdjustment: false
    };

    setKnoxData(recalculateBalances(updatedData, index));
  };

  const handleAdjustedAmountChange = (index, value) => {
    const updatedData = [...knoxData];

    // Allow empty string for editing
    if (value === '') {
      updatedData[index] = {
        ...updatedData[index],
        adjustedAmount: '',
        manualAdjustment: true
      };
      setKnoxData(updatedData);
      return; // ✅ DON'T RECALCULATE YET - let user finish typing
    }

    const newAmount = parseFloat(value);

    // Skip validation if not a valid number
    if (isNaN(newAmount)) {
      return;
    }

    const currentRow = updatedData[index];

    // Check if IRM and SB are selected
    if (!currentRow.irmNumber || !currentRow.shippingBillNumber) {
      toast.error('Please select both IRM and Shipping Bill first');
      return;
    }

    // Get IRM and SB data
    let irmOption = irmOptions.find(opt => opt.value === currentRow.irmNumber);
    let sbOption = sbOptions.find(opt => opt.value === currentRow.shippingBillNumber);

    // Fallback to row data if not in options
    let availableIRMBalance = irmOption
      ? irmOption.data.balance
      : currentRow.irmBalanceAmount;

    let availableSBBalance = sbOption
      ? sbOption.data.invoiceValue
      : currentRow.invoiceValue;

    // ✅ FIX: Calculate available balance considering OTHER rows
    for (let i = 0; i < updatedData.length; i++) {
      if (i !== index) { // Skip current row
        const rowAdjustedAmount = parseFloat(updatedData[i].adjustedAmount) || 0;

        // ✅ KEY FIX: Only reduce for UNSAVED rows
        // Saved rows are already reflected in the balance from API
        if (!isSavedRow(updatedData[i]) && rowAdjustedAmount > 0) {
          if (updatedData[i].irmNumber === currentRow.irmNumber) {
            availableIRMBalance -= rowAdjustedAmount;
          }
          if (updatedData[i].shippingBillNumber === currentRow.shippingBillNumber) {
            availableSBBalance -= rowAdjustedAmount;
          }
        }
      }
    }

    const maxAmount = Math.min(availableIRMBalance, availableSBBalance);

    if (newAmount > maxAmount) {
      toast.error(`Adjusted amount cannot exceed ${maxAmount.toLocaleString()}`);
      return;
    }

    updatedData[index] = {
      ...updatedData[index],
      adjustedAmount: newAmount,
      manualAdjustment: true
    };

    setKnoxData(recalculateBalances(updatedData, index));
  };

  const handleFieldChange = (index, field, value) => {
    const updatedData = [...knoxData];
    updatedData[index] = {
      ...updatedData[index],
      [field]: value
    };
    setKnoxData(updatedData);
  };

  const addNewRow = () => {
    const newRow = createEmptyRow();
    setKnoxData([...knoxData, newRow]);
  };

  const deleteRow = (index) => {
    const updatedData = knoxData.filter((_, i) => i !== index);
    setKnoxData(recalculateBalances(updatedData, 0));
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSuccessMessage(null);
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete all Knox table data for this period? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.post(
        `/api/v1/ebrc/client/knock/off/delete/${period_id}`
      );

      if (response.data.type === 'success') {
        toast.success('Knox table data deleted successfully!');
        // Reset to empty row
        setKnoxData([createEmptyRow(1)]);
      } else {
        toast.error(response.data.message || 'Failed to delete data');
      }
    } catch (err) {
      console.error('Error deleting data:', err);
      toast.error(err.response?.data?.message || 'Error deleting data');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setShowConfirmModal(true);
  };

  const confirmCancelEdit = async () => {
    setShowConfirmModal(false);
    setIsEditing(false);
    // Reload data to revert changes
    await fetchData();
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      // Validate data
      const validRows = knoxData.filter(row => row.irmNumber && row.shippingBillNumber);
      if (validRows.length === 0) {
        setError('Please add at least one IRM and Shipping Bill mapping before saving');
        return;
      }

      const duplicates = findDuplicateCombinations(knoxData);
      if (duplicates.length > 0) {
        const errorMsg = duplicates.map(d =>
          `Row ${d.rowIndex}: Duplicate combination - IRM ${d.irmNumber} + SB ${d.shippingBillNumber} already exists in row ${d.firstOccurrence}`
        ).join('\n');
        setError(`Duplicate IRM + Shipping Bill combinations detected:\n${errorMsg}`);
        toast.error('Cannot save: Duplicate IRM + SB combinations found');
        setSaving(false);
        return;
      }

      // ADD THIS - Currency validation before save
      const currencyMismatches = [];
      for (let i = 0; i < validRows.length; i++) {
        const row = validRows[i];
        const irmOption = irmOptions.find(opt => opt.value === row.irmNumber);
        if (irmOption && row.currency && irmOption.data.currency) {
          if (!validateCurrencyMatch(irmOption.data.currency, row.currency)) {
            currencyMismatches.push({
              rowIndex: i + 1,
              irmNumber: row.irmNumber,
              sbNumber: row.shippingBillNumber,
              irmCurrency: irmOption.data.currency,
              sbCurrency: row.currency
            });
          }
        }
      }

      if (currencyMismatches.length > 0) {
        const errorMsg = currencyMismatches.map(m =>
          `Row ${m.rowIndex}: IRM ${m.irmNumber} (${m.irmCurrency}) ≠ SB ${m.sbNumber} (${m.sbCurrency})`
        ).join('\n');
        setError(`Currency mismatch detected:\n${errorMsg}`);
        toast.error('Cannot save: Currency mismatch in one or more rows');
        setSaving(false);
        return;
      }

      // Prepare data for API
      const saveData = validRows.map(row => ({
        mongo_id: row.id && typeof row.id === 'string' && row.id.length === 24 ? row.id : null,
        irm_id: row.irm_id, // Already stored in state
        shipping_bill_id: row.shipping_bill_id, // Already stored in state
        shippingBillNumber: row.shippingBillNumber, // Backend expects this
        adjustedAmount: row.adjustedAmount, // Match frontend field name
        availableIRMBalance: row.availableIRMBalance, // Frontend calculated
        availableSBBalance: row.availableSBBalance, // Frontend calculated
        pendingBalanceInIRM: row.pendingBalanceInIRM, // Frontend calculated
        sbBalance: row.sbBalance, // Frontend calculated
        shippingBillDate: row.shippingBillDate,
        invoiceNumber: row.invoiceNumber,
        invoiceDate: row.invoiceDate,
        consigneeName: row.consigneeName,
        buyerName: row.buyerName,
        gstNumber: row.gstNumber,
        invoiceValue: row.invoiceValue,
        fobValue: row.fobValue,
        currency: row.currency,
        portCode: row.portCode,
        sbAdCode: row.sbAdCode,
        brcNumber: row.brcNumber,
        brcDate: row.brcDate,
        attachments: row.attachments,
        remarks: row.remarks
      }));

      const response = await axiosInstance.post(
        `/api/v1/ebrc/client/knockOff/table/save/${period_id}`,
        { knox_data: saveData }
      );
      console.log("Payload-knox_data:", saveData);
      if (response.data.type === 'success') {
        setSuccessMessage('Knock-Off table data saved successfully!');
        toast.success('Knock-Off table data saved successfully!');
        setIsEditing(false);
        // Reload data
        await fetchData();
      } else {
        setError(response.data.message || 'Failed to save data');
        toast.error(response.data.message || 'Failed to save data');
      }
    } catch (err) {
      console.error('Error saving data:', err);
      const errorMsg = err.response?.data?.message || 'Error saving data: ' + err.message;
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitPreface = async () => {
    try {
      setSubmitting(true);

      const response = await axiosInstance.post(
        `/api/v1/ebrc/client/knockOff/submit/preface/${period_id}`
      );

      if (response.data.type === 'success') {
        toast.success(response.data.message);
        setClientHasSubmitted(true);
        await fetchData();
      } else if (response.data.type === 'info') {
        toast.info(response.data.message);
      } else {
        toast.error(response.data.message || 'Failed to submit');
      }
    } catch (error) {
      console.error("Submitting Error:", error);
      toast.error(error.response?.data?.message || "Submitting failed");
    } finally {
      setSubmitting(false);
    }
  };



  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" variant="primary">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-2">Loading Knock-Off Table...</p>
      </div>
    );
  }

  const handleViewCertificate = (row) => {
    if (row.certificateUrl) {
      setSelectedCertificate({
        url: row.certificateUrl,
        downloadUrl: row.certificateDownloadUrl,
        filename: row.certificateFilename || 'certificate.pdf',
        brcNumber: row.brcNumber,
        sbNumber: row.shippingBillNumber
      });
      setShowCertificateModal(true);
    } else {
      toast.error('Certificate not available');
    }
  };

  const formatDateOnly = (dateValue) => {
    if (!dateValue) return 'N/A';

    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return 'N/A';

    return date.toISOString().split('T')[0];
  };

  const handleExportToExcel = async () => {
    try {
      // Filter out empty rows (rows without both IRM and SB)
      const validData = knoxData.filter(row => row.irmNumber && row.shippingBillNumber);

      if (validData.length === 0) {
        toast.error('No data to export');
        return;
      }

      toast.loading('Generating Excel file...');

      const result = await exportKnoxTableToExcel(validData, periodData);

      toast.dismiss();

      if (result.success) {
        toast.success('Excel file downloaded successfully!');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to generate Excel file');
      console.error('Excel export error:', error);
    }
  };

  const hasKnoxData = knoxData.some(row => row.irmNumber && row.shippingBillNumber);

  const isApproved = periodData?.knockoff_approved === 1;
  const isRejected = periodData?.knockoff_rejected === 1;

  // ADD THESE - Permission checks
  const canEdit = clientEditPermission && !clientSubmissionVerified;
  const isLocked = !canEdit || isApproved
  const showSubmitButton = hasKnoxData && clientEditPermission && !clientHasSubmitted && !clientSubmissionVerified;
  const showWaitingMessage = clientHasSubmitted && !clientSubmissionVerified;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4>Knock-Off Table - IRM to Shipping Bill Mapping</h4>
          <small className="text-muted">
            Map IRM records with Shipping Bills to track remittance allocation
          </small>
        </div>
        <div>
          {!isEditing ? (
            <>
              <Button
                variant="danger"
                onClick={handleDelete}
                className="me-2 d-inline-flex align-items-center gap-1 fw-bold"
                disabled={isLocked || !hasKnoxData}
              >
                <MdDelete /> Delete All
              </Button>
              <Button
                variant="primary"
                onClick={handleEdit}
                className="me-2 d-inline-flex align-items-center gap-1 fw-bold"
                disabled={isLocked}
              >
                <MdEditSquare /> Edit
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                onClick={handleSave}
                disabled={saving}
                className="me-2"
              >
                {saving ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-save"></i> Save
                  </>
                )}
              </Button>
              <Button
                variant="danger"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                Cancel
              </Button>
            </>
          )}
        </div>
      </div>

      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert variant="success" onClose={() => setSuccessMessage(null)} dismissible>
          {successMessage}
        </Alert>
      )}

      {/* ADD THIS - Permission Status Messages */}
      {!clientEditPermission && (
        <Alert variant="warning" className="mb-3">
          <i className="bi bi-lock-fill me-2"></i>
          <strong>No Edit Permission:</strong> You currently do not have permission to edit the Knox table.
          Please contact the Preface team to request access.
        </Alert>
      )}

      {showWaitingMessage && (
        <Alert variant="info" className="mb-3">
          <i className="bi bi-hourglass-split me-2"></i>
          <strong>Submitted for Verification:</strong> Your Knock Off table has been submitted to Preface team for verification.
          You can still make changes until they verify your submission.
          {periodData?.client_last_submission_date && (
            <div className="ms-2 text-muted">
              Last submitted: {new Date(periodData.client_last_submission_date).toLocaleString()}
            </div>
          )}
        </Alert>
      )}

      {clientSubmissionVerified && !isApproved && (
        <Alert variant="success" className="mb-3">
          <i className="bi bi-check-circle-fill me-2"></i>
          <strong>Verified:</strong> Preface team has verified your submission. Awaiting final approval.
        </Alert>
      )}

      {isApproved && (
        <Alert variant="success" className="mb-3">
          <i className="bi bi-check-circle-fill me-2"></i>
          <strong>Approved:</strong> Your Knox table has been approved by the director.
        </Alert>
      )}

      {isRejected && (
        <Alert variant="danger" className="mb-3">
          <i className="bi bi-x-circle-fill me-2"></i>
          <strong>Rejected:</strong> Your Knox table has been rejected. Please make necessary changes and resubmit.
        </Alert>
      )}

      {isEditing && (
        <>
          <Alert variant="info">
            <strong className='me-1'>Instructions: </strong> Select IRM Number first, then Shipping Bill Number.
            The adjusted amount will auto-fill with the minimum of available IRM balance and Shipping Bill value.
            You can edit the adjusted amount manually if needed.
          </Alert>
          <div className="mb-3">
            <Button variant="primary" size="sm" onClick={addNewRow} disabled={isLocked}>
              <i className="bi bi-plus-circle"></i> Add New Row
            </Button>
          </div>
        </>
      )}

      <div className="table-responsive" style={{ maxHeight: '600px', overflowY: 'auto', overflowX: 'auto', position: 'relative' }}>
        <style>
          {`
      .knox-table th,
      .knox-table td {
        white-space: nowrap;
        padding: 0.75rem !important;
      }
      
      .knox-table th {
        font-weight: 600;
      }
      
      /* Sticky remarks column */
      .knox-table .remarks-column,
      .knox-table .remarks-column-header {
        position: sticky !important;
        right: 0;
        background-color: #fff9e6;  /* Changed - Soft yellow for header */
        z-index: 10;
        box-shadow: -4px 0 8px rgba(0,0,0,0.15);  /* Enhanced shadow for visibility */
        border-left: 2px solid #ffd54f;  /* Add border to create gap effect */
      }

      .knox-table tbody .remarks-column {
        background-color: #fffdf5;  /* Changed - Ultra-light cream for body */
        border-left: 2px solid #ffd54f;  /* Add border to create gap effect */
      }

      /* Sticky action column when editing */
      .knox-table .action-column,
      .knox-table .action-column-header {
        position: sticky !important;
        right: 0;
        background-color: #fff9e6;  /* Changed - Soft yellow for header */
        z-index: 10;
        box-shadow: -4px 0 8px rgba(0,0,0,0.15);  /* Enhanced shadow for visibility */
        border-left: 1px solid #ffd54f;  /* Add border to create gap effect */
      }

      .knox-table tbody .action-column {
        background-color: #fffdf5;  /* Changed - Ultra-light cream for body */
        border-left: 1px solid #ffd54f;  /* Add border to create gap effect */
      }

      /* When editing, remarks should be second from right */
      .knox-table.editing .remarks-column,
      .knox-table.editing .remarks-column-header {
        right: 100px;  /* Increased from 80px to match action column width */
        border-left: 1px solid #ffd54f;
        border-right: none;  /* Remove right border */
      }

      /* Add specific width to action column for consistency */
      .knox-table .action-column,
      .knox-table .action-column-header {
        position: sticky !important;
        right: 0;
        background-color: #fff9e6;
        z-index: 10;
        box-shadow: -4px 0 8px rgba(0,0,0,0.15);
        border-left: 1px solid #ffd54f;
        min-width: 100px;  /* Add this - ensures consistent width */
        max-width: 100px;  /* Add this - ensures consistent width */
        width: 100px;  /* Add this - ensures consistent width */
      }
      
      .knox-table thead {
        position: sticky;
        top: 0;
        z-index: 20;
      }
    `}
        </style>
        <Table
          className={`core-ui-table seperate-border knox-table ${isEditing ? 'editing' : ''}`}
          size="sm"
        >
          <thead className="table-light">
            <tr>
              <th className="text-center">S.No</th>
              <th className="text-left">IRM Reference</th>
              <th className="text-left">IRM Date</th>
              <th className="text-end">Available IRM Balance</th>
              <th className="text-left">Remitter Name</th>
              <th className="text-left">AD Code</th>
              <th className="text-left">Shipping Bill Number</th>
              <th className="text-end">Available SB Balance</th>
              <th className="text-left">Shipping Bill Date</th>
              <th className="text-left">Invoice Number</th>
              <th className="text-left">Invoice Date</th>
              <th className="text-end">FOB Value</th>
              <th className="text-left">Consignee Name</th>
              <th className="text-left">Buyer Name</th>
              <th className="text-left">GST Number</th>
              <th className="text-left">Currency</th>
              <th className="text-left">Port Code</th>
              <th className="text-left">SB AD Code</th>
              <th className="text-end">Adjusted Amount</th>
              <th className="text-end">Pending Balance in IRM</th>
              <th className="text-end">SB Balance</th>
              <th className="text-left">BRC Number</th>
              <th className="text-left">BRC Date</th>
              <th className="text-left">Certificate</th>
              <th className="text-left remarks-column-header ">Remarks</th>
              {isEditing && <th className="text-center action-column-header">Action</th>}
            </tr>
          </thead>
          <tbody>
            {knoxData.length === 0 ? (
              <tr>
                <td colSpan={isEditing ? "25" : "24"} className="text-center text-muted py-4">
                  No Knock-Off table data found. Click "Add New Row" to start mapping.
                </td>
              </tr>
            ) : (
              knoxData.map((row, index) => (
                <tr key={row.id || index}
                  style={{
                    backgroundColor: row.brcGenerated === 1 ? '#f8f9fa' : 'transparent',
                    opacity: row.brcGenerated === 1 ? 0.7 : 1
                  }}
                  title={row.brcGenerated === 1 ? 'This row is locked - BRC already generated' : ''}
                >
                  <td className="text-center">{index + 1}</td>

                  {/* IRM Reference */}
                  <td className="text-left">
                    {isEditing ? (
                      <Select
                        value={
                          row.irmNumber
                            ? irmOptions.find(opt => opt.value === row.irmNumber) || createIRMOptionFromRow(row)
                            : null
                        }
                        onChange={(option) => handleIRMChange(index, option)}
                        options={irmOptions}
                        onInputChange={(value) => setIrmSearchTerm(value)}
                        placeholder="Search IRM..."
                        isClearable
                        isSearchable
                        closeMenuOnScroll={(event) => {
                          if (document.querySelector('[role="listbox"]')?.contains(event.target)) {
                            return false;
                          }
                          return true;
                        }}
                        isLoading={irmLoading}
                        isDisabled={row.brcGenerated === 1}
                        loadingMessage={() => "Searching IRMs..."}
                        noOptionsMessage={() => irmSearchTerm ? "No IRMs found" : "Type to search IRMs"}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minWidth: '280px',
                            fontSize: '0.875rem'
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999
                          })
                        }}
                      />

                    ) : (
                      <strong>{row.irmNumber}</strong>
                    )}
                  </td>

                  <td className="text-left">{row.irmDate || 'N/A'}</td>

                  <td className="text-left">
                    {row.availableIRMBalance >= 0
                      ? `${row.availableIRMBalance.toLocaleString()} ${row.currency || 'USD'}`
                      : (row.irmBalanceAmount > 0 ? `${row.irmBalanceAmount.toLocaleString()} ${row.currency || 'USD'}` : '')
                    }
                  </td>

                  <td className="text-left">{row.remitterName || 'N/A'}</td>
                  <td className="text-left">{row.adCode || 'N/A'}</td>

                  {/* Shipping Bill Number */}
                  <td className="text-left">
                    {isEditing ? (
                      <Select
                        value={
                          row.shippingBillNumber
                            ? sbOptions.find(opt => opt.value === row.shippingBillNumber) || createSBOptionFromRow(row)
                            : null
                        }
                        onChange={(option) => handleSBChange(index, option)}
                        options={sbOptions}
                        onInputChange={(value) => setSbSearchTerm(value)}
                        placeholder="Search Shipping Bill..."
                        isClearable
                        isSearchable
                        closeMenuOnScroll={(event) => {
                          if (document.querySelector('[role="listbox"]')?.contains(event.target)) {
                            return false;
                          }
                          return true;
                        }}
                        isDisabled={!row.irmNumber || row.brcGenerated === 1}
                        isLoading={sbLoading}
                        loadingMessage={() => "Searching Shipping Bills..."}
                        noOptionsMessage={() => sbSearchTerm ? "No Shipping Bills found" : "Type to search Shipping Bills"}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        styles={{
                          control: (base) => ({
                            ...base,
                            minWidth: '280px',
                            fontSize: '0.875rem'
                          }),
                          menuPortal: (base) => ({
                            ...base,
                            zIndex: 9999
                          })
                        }}
                      />
                    ) : (
                      <strong>{row.shippingBillNumber}</strong>
                    )}
                  </td>

                  <td className="text-left">
                    {row.availableSBBalance >= 0 ? `${row.availableSBBalance.toLocaleString()} ${row.currency || 'USD'}` : ''}
                  </td>

                  <td className="text-left">{row.shippingBillDate || 'N/A'}</td>
                  <td className="text-left">{row.invoiceNumber || 'N/A'}</td>
                  <td className="text-left">{row.invoiceDate || 'N/A'}</td>
                  <td className="text-end">
                    {row.fobValue > 0 ? `${row.fobValue.toLocaleString()} ${row.currency || 'USD'}` : 'N/A'}
                  </td>
                  <td className="text-left">{row.consigneeName || 'N/A'}</td>
                  <td className="text-left">{row.buyerName || 'N/A'}</td>
                  <td className="text-left">{row.gstNumber || 'N/A'}</td>
                  <td className="text-left">{row.currency || 'N/A'}</td>
                  <td className="text-left">{row.portCode || 'N/A'}</td>
                  <td className="text-left">{row.sbAdCode || 'N/A'}</td>

                  {/* Adjusted Amount */}
                  <td className="text-left">
                    {isEditing && row.irmNumber && row.shippingBillNumber ? (
                      <Form.Control
                        type="text"
                        size="sm"
                        value={row.adjustedAmount === '' ? '' : row.adjustedAmount}
                        onChange={(e) => {
                          // Only allow numbers and decimal point
                          const value = e.target.value;
                          if (value === '' || /^\d*\.?\d*$/.test(value)) {
                            handleAdjustedAmountChange(index, value);
                          }
                        }}
                        onBlur={() => {
                          const updatedData = [...knoxData];
                          if (updatedData[index].adjustedAmount === '' || updatedData[index].adjustedAmount === 0) {
                            updatedData[index].adjustedAmount = 0;
                            setKnoxData(recalculateBalances(updatedData, index));
                          }
                        }}
                        className="text-end"
                        style={{ minWidth: '120px' }}
                        disabled={row.brcGenerated === 1}
                        readOnly={row.brcGenerated === 1}
                        placeholder="0.00"
                      />
                    ) : (
                      <span>
                        {row.adjustedAmount > 0 ? `${row.adjustedAmount.toLocaleString()} ${row.currency || 'USD'}` : 'N/A'}
                      </span>
                    )}
                  </td>

                  <td className="text-left">
                    <Badge bg={row.pendingBalanceInIRM === 0 ? 'success' : 'warning'}>
                      {row.pendingBalanceInIRM > 0 ? `${row.pendingBalanceInIRM.toLocaleString()} ${row.currency || 'USD'}` : '0'}
                    </Badge>
                  </td>

                  <td className="text-left">
                    <Badge bg={row.sbBalance === 0 ? 'success' : 'warning'}>
                      {row.sbBalance > 0 ? `${row.sbBalance.toLocaleString()} ${row.currency || 'USD'}` : '0'}
                    </Badge>
                  </td>

                  {/* BRC Number */}
                  <td className="text-left">
                    {row.brcNumber || 'N/A'}
                  </td>


                  {/* BRC Date */}
                  <td className="text-left">
                    {formatDateOnly(row.brcDate)}
                  </td>

                  {/* Attachments */}
                  <td className="text-left">
                    {row.certificateUploadStatus === 'success' && row.certificateUrl ? (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleViewCertificate(row)}
                      >
                        <i className="bi bi-file-earmark-pdf"></i> View Certificate
                      </Button>
                    ) : row.certificateUploadStatus === 'pending' ? (
                      <Badge bg="warning">Pending</Badge>
                    ) : row.certificateUploadStatus === 'failed' ? (
                      <Badge bg="danger">Failed</Badge>
                    ) : (
                      'N/A'
                    )}
                  </td>

                  {/* Remarks - Sticky Column */}
                  <td className="text-left remarks-column" style={{ minWidth: '250px' }}>
                    <small className={row.pendingBalanceInIRM === 0 && row.sbBalance === 0 ? 'text-success fw-bold' : ''}>
                      {row.remarks || 'N/A'}
                    </small>
                  </td>

                  {/* Action - Sticky when editing */}
                  {isEditing && (
                    <td className="text-center action-column">
                      <Button
                        variant="danger"
                        disabled={row.brcGenerated === 1}
                        onClick={() => deleteRow(index)}
                      >
                        <MdDelete />
                      </Button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
      {hasKnoxData && (
        <div className="mt-2 mb-1">
          Total Records: <strong>{totalCount}</strong>
        </div>
      )}
      {hasKnoxData && (
        <div className="my-3 d-flex justify-content-between align-items-center">
          {/* LEFT SIDE - Export Button */}
          <Button
            variant="primary"
            className="fw-bold d-flex align-items-center gap-2"
            onClick={handleExportToExcel}
            disabled={loading}
          >
            <BsFileEarmarkSpreadsheet size={18} />
            Export to Excel
          </Button>

          {/* RIGHT SIDE - Submit Button */}
          <div className="d-flex gap-2">
            {showSubmitButton && (
              <Button
                variant="primary"
                className="fw-bold px-4 shadow-sm d-flex align-items-center gap-1"
                disabled={submitting || isEditing}
                onClick={handleSubmitPreface}
              >
                <TiTick />
                {submitting ? 'Submitting...' : 'Submit to Preface for Verification'}
              </Button>
            )}

            {clientHasSubmitted && !clientSubmissionVerified && (
              <Badge bg="warning" className="p-3 d-flex align-items-center">
                <i className="bi bi-hourglass-split me-2"></i>
                Preface Team working on Verification
              </Badge>
            )}

            {clientSubmissionVerified && !isApproved && (
              <Badge bg="primary" className="p-3 d-flex align-items-center">
                <i className="bi bi-check-circle-fill me-2"></i>
                Verified - Awaiting Final Approval
              </Badge>
            )}

            {isApproved && (
              <Badge bg="primary" className="p-3 d-flex align-items-center">
                <i className="bi bi-check-circle-fill me-2"></i>
                Approved by Director
              </Badge>
            )}
          </div>
        </div>
      )}



      {/* Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Cancel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to cancel? All unsaved changes will be lost.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="Primary" onClick={() => setShowConfirmModal(false)}>
            No, Continue Editing
          </Button>
          <Button variant="danger" onClick={confirmCancelEdit}>
            Yes, Cancel Changes
          </Button>
        </Modal.Footer>
      </Modal>
      <CertificateViewModal
        show={showCertificateModal}
        onHide={() => setShowCertificateModal(false)}
        certificate={selectedCertificate}
      />
    </div>
  );
};

export default KnockOffTable;